/**
 * API client for calling Cloud Functions
 */

const FUNCTIONS_BASE_URL = 'https://us-central1-asili-guide.cloudfunctions.net';

// Session storage key
const SESSION_KEY = 'snapshot_admin_session';

interface ApiResponse<T> {
  result?: T;
  error?: {
    message: string;
    status: string;
  };
}

/**
 * Call a Cloud Function
 */
async function callFunction<T>(name: string, data: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });

  const result: ApiResponse<T> = await response.json();

  if (result.error) {
    throw new Error(result.error.message || 'Unknown error');
  }

  return result.result as T;
}

// ============================================
// Admin Authentication
// ============================================

interface AdminLoginResponse {
  success: boolean;
  token: string;
  admin: {
    id: string;
    email: string;
    role: string;
  };
  expiresAt: string;
}

interface AdminSession {
  token: string;
  admin: {
    id: string;
    email: string;
    role: string;
  };
  expiresAt: string;
}

export async function adminLogin(email: string, password: string): Promise<AdminSession> {
  const response = await callFunction<AdminLoginResponse>('adminLogin', { email, password });

  // Store session
  const session: AdminSession = {
    token: response.token,
    admin: response.admin,
    expiresAt: response.expiresAt,
  };

  localStorage.setItem(SESSION_KEY, JSON.stringify(session));

  return session;
}

export async function adminLogout(): Promise<void> {
  const session = getAdminSession();

  if (session) {
    try {
      await callFunction('adminLogout', { token: session.token });
    } catch {
      // Ignore logout errors
    }
  }

  localStorage.removeItem(SESSION_KEY);
}

export function getAdminSession(): AdminSession | null {
  const stored = localStorage.getItem(SESSION_KEY);
  if (!stored) return null;

  try {
    const session: AdminSession = JSON.parse(stored);

    // Check if expired
    if (new Date(session.expiresAt) < new Date()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }

    return session;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function isAdminLoggedIn(): boolean {
  return getAdminSession() !== null;
}

// ============================================
// Admin Dashboard
// ============================================

interface DashboardStats {
  total: number;
  preview: number;
  paid: number;
  completed: number;
  refunded: number;
  activeInvites: number;
  directPurchases: number;
  inviteBasedPurchases: number;
}

interface RiskDistribution {
  low: number;
  medium: number;
  high: number;
}

interface RecentSnapshot {
  id: string;
  agencyName: string;
  email?: string;
  status: string;
  riskBand?: string;
  totalScore?: number;
  createdAt?: string;
}

interface AuditLog {
  id: string;
  action: string;
  snapshotId?: string;
  timestamp: string;
  details?: Record<string, unknown>;
}

interface DashboardData {
  stats: DashboardStats;
  riskDistribution: RiskDistribution;
  recentSnapshots: RecentSnapshot[];
  auditLogs: AuditLog[];
}

export async function getAdminDashboard(): Promise<DashboardData> {
  const session = getAdminSession();
  if (!session) throw new Error('Not authenticated');

  return callFunction<DashboardData>('getAdminDashboard', { token: session.token });
}

// ============================================
// Snapshots
// ============================================

interface SnapshotListItem {
  id: string;
  agencyName: string;
  status: string;
  riskBand?: string;
  totalScore?: number;
  email?: string;
  createdAt?: string;
  completedAt?: string;
  paidAt?: string;
}

export async function listSnapshots(status?: string, limit?: number): Promise<SnapshotListItem[]> {
  const session = getAdminSession();
  if (!session) throw new Error('Not authenticated');

  const response = await callFunction<{ snapshots: SnapshotListItem[] }>('listSnapshots', {
    token: session.token,
    status,
    limit,
  });

  return response.snapshots;
}

interface SnapshotDetails {
  snapshot: Record<string, unknown>;
  auditLogs: AuditLog[];
}

export async function getSnapshotDetails(snapshotId: string): Promise<SnapshotDetails> {
  const session = getAdminSession();
  if (!session) throw new Error('Not authenticated');

  return callFunction<SnapshotDetails>('getSnapshotDetails', {
    token: session.token,
    snapshotId,
  });
}

// ============================================
// Pending Reports & PDF Release
// ============================================

interface PendingReport {
  id: string;
  agencyName: string;
  email?: string;
  state?: string;
  riskBand?: string;
  totalScore?: number;
  completedAt: string;
  hoursSinceCompletion: number;
  hoursUntilAutoRelease: number;
  hasPdf: boolean;
  pdfStatus: string;
}

export async function getPendingReports(): Promise<PendingReport[]> {
  const session = getAdminSession();
  if (!session) throw new Error('Not authenticated');

  const response = await callFunction<{ pendingReports: PendingReport[] }>('getPendingReports', {
    token: session.token,
  });

  return response.pendingReports;
}

interface ReleasePdfResponse {
  success: boolean;
  message: string;
  releasedAt: string;
  emailSent: boolean;
  emailError?: string;
}

export async function releasePdfReport(snapshotId: string, sendEmail: boolean = true): Promise<ReleasePdfResponse> {
  const session = getAdminSession();
  if (!session) throw new Error('Not authenticated');

  return callFunction<ReleasePdfResponse>('releasePdfReport', {
    token: session.token,
    snapshotId,
    sendEmail,
  });
}

export async function triggerPdfGeneration(snapshotId: string): Promise<{ success: boolean; status: string }> {
  return callFunction<{ success: boolean; status: string }>('queuePdfGeneration', { snapshotId });
}

export { type PendingReport };

// ============================================
// Invites
// ============================================

interface CreateInviteResponse {
  inviteId: string;
  token: string;
  inviteUrl: string;
  expiresAt: string;
}

export async function createInvite(expiryDays: number = 14): Promise<CreateInviteResponse> {
  const session = getAdminSession();
  if (!session) throw new Error('Not authenticated');

  return callFunction<CreateInviteResponse>('createInvite', {
    adminToken: session.token,
    expiryDays,
  });
}

interface InviteListItem {
  id: string;
  status: string;
  tokenPrefix: string;
  createdAt: string | null;
  expiresAt: string | null;
  createdBy: string;
  accessCount: number;
  campaign: string | null;
}

export async function listInvites(status?: 'active' | 'expired' | 'used'): Promise<InviteListItem[]> {
  const session = getAdminSession();
  if (!session) throw new Error('Not authenticated');

  const response = await callFunction<{ invites: InviteListItem[] }>('listInvites', {
    adminToken: session.token,
    status,
  });

  return response.invites;
}

// ============================================
// Public APIs (no auth required)
// ============================================

interface ValidateInviteResponse {
  valid: boolean;
  snapshotId?: string;
  status?: string;
}

export async function validateInvite(token: string): Promise<ValidateInviteResponse> {
  return callFunction<ValidateInviteResponse>('validateInvite', { token });
}

// ============================================
// Drip invite APIs (personalized links from campaign emails)
// ============================================

interface ResolveDripInviteResponse {
  valid: boolean;
  state: string;
  email: string;
}

export async function resolveDripInvite(token: string, campaign?: string | null): Promise<ResolveDripInviteResponse> {
  return callFunction<ResolveDripInviteResponse>('resolveDripInvite', { token, campaign: campaign || undefined });
}

interface StartFromInviteResponse {
  success: boolean;
  snapshotId: string;
  resumed: boolean;
}

export async function startFromInvite(
  token: string,
  campaign?: string | null,
  itemCount?: number
): Promise<StartFromInviteResponse> {
  return callFunction<StartFromInviteResponse>('startFromInvite', { token, campaign: campaign || undefined, itemCount });
}

// ============================================
// Checkout APIs
// ============================================

interface CreateSnapshotResponse {
  success: boolean;
  snapshotId: string;
}

export async function createSnapshot(email: string, state: string, itemCount?: number): Promise<CreateSnapshotResponse> {
  return callFunction<CreateSnapshotResponse>('createSnapshot', { email, state, itemCount });
}

interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export async function createCheckoutSession(
  snapshotId: string,
  email: string,
  returnUrl?: string,
  state?: string,
  itemCount?: number
): Promise<CreateCheckoutSessionResponse> {
  return callFunction<CreateCheckoutSessionResponse>('createCheckoutSession', {
    snapshotId,
    email,
    returnUrl,
    state,
    itemCount,
  });
}

// ============================================
// Results APIs
// ============================================

interface RiskItem {
  itemId: string;
  text: string;
  category?: string;
  severity: string;
}

interface CategoryScore {
  score: number;
  maxPossible: number;
  percentage: number;
}

interface ScoringResult {
  totalScore: number;
  riskBand: 'low' | 'medium' | 'high';
  categoryScores: Record<string, CategoryScore>;
  breakdown: {
    compliant: number;
    atRisk: number;
    nonCompliant: number;
    unknown: number;
  };
  calculatedAt?: string;
  version: string;
}

interface NarrativeResult {
  executiveSummary: string;
  categoryNarratives: Record<string, string>;
  recommendations: string[];
  generatedAt?: string;
  source: 'openai' | 'fallback';
}

interface AgencyProfile {
  name: string;
  state: string;
  licenseNumber?: string;
  numberOfStaff?: number;
  servicesOffered?: string[];
  contactEmail?: string;
  contactPhone?: string;
}

interface PdfStatus {
  status: 'generating' | 'completed' | 'failed';
  downloadUrl: string | null;
  expiresAt?: string;
  // Delayed delivery fields
  releasedAt?: string;
  releaseMethod?: 'admin_manual' | 'auto_email' | 'auto_unlock';
  emailedAt?: string;
}

interface SnapshotResultsData {
  id: string;
  // Status field indicates if results are released or pending
  status?: 'pending_release';
  agencyProfile: AgencyProfile;
  scoring: ScoringResult;
  narrative?: NarrativeResult;
  checklistResponses: Record<string, { status: string; notes?: string }>;
  immediateRisks: RiskItem[];
  futureRisks: RiskItem[];
  pdf: PdfStatus | null;
  completedAt?: string;
  // When results will be available (if pending_release)
  resultsReleaseAt?: string;
  message?: string;
}

export async function getSnapshotResults(snapshotId: string): Promise<SnapshotResultsData> {
  return callFunction<SnapshotResultsData>('getSnapshotResults', { snapshotId });
}

interface PdfQueueResponse {
  success: boolean;
  status?: 'queued';
  pdfUrl?: string;
  cached?: boolean;
  message?: string;
}

export async function queuePdfGeneration(snapshotId: string, isPreview = false): Promise<PdfQueueResponse> {
  return callFunction<PdfQueueResponse>('queuePdfGeneration', { snapshotId, isPreview });
}

interface PdfDownloadResponse {
  success: boolean;
  status?: 'generating';
  downloadUrl?: string;
  expiresAt?: string;
  message?: string;
}

export async function getPdfDownloadUrl(snapshotId: string): Promise<PdfDownloadResponse> {
  return callFunction<PdfDownloadResponse>('getPdfDownloadUrl', { snapshotId });
}

// Generate AI narratives
interface GenerateNarrativesResponse {
  success: boolean;
  narrative: NarrativeResult;
  cached?: boolean;
}

export async function generateNarratives(snapshotId: string): Promise<GenerateNarrativesResponse> {
  return callFunction<GenerateNarrativesResponse>('generateNarratives', { snapshotId });
}

// ============================================
// Assessment APIs
// ============================================

interface SnapshotForAssessment {
  id: string;
  status: string;
  agencyProfile: Record<string, unknown>;
  onboardingAssessment: Record<string, unknown>;
  checklistResponses: Record<string, { status: string; notes?: string }>;
  diagnosticAnswers?: Record<string, unknown>;
  onboardingDiagnosticAnswers?: Record<string, unknown>;
  assessmentStarted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export async function getSnapshotForAssessment(snapshotId: string): Promise<SnapshotForAssessment> {
  return callFunction<SnapshotForAssessment>('getSnapshotForAssessment', { snapshotId });
}

interface UpdateProgressParams {
  snapshotId: string;
  agencyProfile?: Record<string, unknown>;
  onboardingAssessment?: Record<string, unknown>;
  checklistResponses?: Record<string, unknown>;
  diagnosticAnswers?: Record<string, unknown>;
  onboardingDiagnosticAnswers?: Record<string, unknown>;
  assessmentStarted?: boolean;
}

interface UpdateProgressResponse {
  success: boolean;
  updatedAt: string;
}

export async function updateSnapshotProgress(params: UpdateProgressParams): Promise<UpdateProgressResponse> {
  return callFunction<UpdateProgressResponse>('updateSnapshotProgress', params as unknown as Record<string, unknown>);
}

interface SubmitAssessmentResponse {
  success: boolean;
  scoring: ScoringResult;
}

export async function submitAssessment(snapshotId: string, expectedTotal?: number): Promise<SubmitAssessmentResponse> {
  return callFunction<SubmitAssessmentResponse>('submitAssessment', { snapshotId, expectedTotal });
}

// ============================================
// Analytics / Tracking
// ============================================

interface TrackEventParams {
  event: string;
  properties?: Record<string, unknown>;
}

// Anonymous per-tab session ID so the backend can deduplicate page-view
// events (one landing visit that remounts a component three times should
// count once, not three times).
function getAnalyticsSessionId(): string {
  const KEY = 'snapshot_session_id';
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // Storage blocked (private mode etc.) — fall back to a per-load ID.
    return crypto.randomUUID();
  }
}

export async function trackEvent(params: TrackEventParams): Promise<void> {
  try {
    await callFunction('trackEvent', {
      event: params.event,
      properties: params.properties || {},
      timestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer || null,
      sessionId: getAnalyticsSessionId(),
    });
  } catch (err) {
    // Don't throw on tracking errors - just log
    console.warn('Tracking failed:', err);
  }
}

export {
  type AdminSession,
  type DashboardData,
  type SnapshotListItem,
  type CreateInviteResponse,
  type SnapshotResultsData,
  type ScoringResult,
  type NarrativeResult,
  type RiskItem,
  type PdfStatus,
  type AgencyProfile,
};
